import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getInternalUserProfile,
  listInternalUsers,
  updateInternalUserProfile,
  UpdateInternalUserProfileRequest,
} from "../api/internal-users-api";

// Note: there is also a backend endpoint /internal-users/me (and a matching
// `getMyInternalUserProfile` API helper) for fetching the current user's profile
// without needing the user id. We don't expose a hook for it because the page
// always knows the current user's id via the auth provider — fetching by id
// works for both self-edit and admin-editing-someone-else.

export const useInternalUserProfile = (userId: number | null | undefined) =>
  useQuery({
    queryKey: ["internal-user-profile", userId],
    queryFn: () => getInternalUserProfile(userId as number),
    enabled: typeof userId === "number" && userId > 0,
  });

export const useListInternalUsers = (enabled: boolean = true) =>
  useQuery({
    queryKey: ["internal-users", "list"],
    queryFn: listInternalUsers,
    enabled,
  });

export const useUpdateInternalUserProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: number;
      data: UpdateInternalUserProfileRequest;
    }) => updateInternalUserProfile(userId, data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ["internal-user-profile", userId] });
    },
  });
};
